/**
 * Appendix A.3 — Event bus. Single FIFO queue, single-threaded.
 * Handlers may emit; their events are delivered later in the same flush.
 * Handlers are never re-entered (no nested dispatch).
 */

export type Vec2 = { x: number; y: number };

/**
 * Where a blow landed, for the sources that know. `x, y` is the contact point already
 * canonicalised onto the torus (AMD-24: it can sit across the seam from the body centre),
 * `dir_x, dir_y` the unit travel direction of the thing that struck.
 *
 * A `hit` carries `null` here when its source has no contact point at all — a status tick,
 * a hazard, a body touching a body. That is absence, not a zero: presentation falls back to
 * the body centre rather than reading (0, 0) as a position.
 */
export interface HitContact {
  x: number;
  y: number;
  dir_x: number;
  dir_y: number;
}

export interface EventPayloads {
  spawn: { entity_id: number; kind: string; def_id: string; pos: Vec2; elite: boolean; affixes: string[] };
  materialize: { entity_id: number };
  death: { entity_id: number; killer_id: number; cause_id: string; pos: Vec2; elite: boolean; def_id: string; threat: number };
  /**
   * `pos` is the struck body's centre and `radius` its hitbox radius: the impact beat is
   * scaled to the body. `contact` is where the blow actually landed, or `null` from a
   * source that has no contact point (§4.9 status ticks, hazards, contact damage).
   */
  hit: { src_id: number; tgt_id: number; weapon_id: string; damage: number; crit: boolean; killed: boolean; depth: number; pos: Vec2; radius: number; contact: HitContact | null };
  player_hit: { source_id: number; amount: number; hp_after: number; telegraph_event_id: number; source_def_id: string };
  player_heal: { amount: number; hp_after: number };
  levelup_offer: { level: number; options: string[]; reroll_index: number };
  levelup_pick: { level: number; picked: string; options: string[] };
  evolution: { weapon_id: string; evolution_id: string };
  pickup: { entity_id: number; kind: 'xp_mote' | 'health' | 'powerup'; value: number; pos: Vec2 };
  dash: { phase: 'start' | 'end'; dir: Vec2 };
  surge_change: { from: number; to: number; cause: string };
  phase_change: { phase_id: string };
  boss_spawn: { boss_id: string; entity_id: number };
  boss_phase: { boss_id: string; index: number };
  boss_defeated: { boss_id: string; fight_duration_s: number };
  hazard: { hazard_id: string; kind: 'telegraph' | 'activate' | 'end'; pos: Vec2; radius: number };
  /**
   * §10.7.4 — the space storm. One event type for the whole arc, discriminated by `kind`.
   *
   * `intensity` is the storm envelope at the emitting tick, 0..1, and is the ONLY number
   * a presentation layer needs: the veil's opacity, the music bed's depth and the radar's
   * interference all read it rather than re-deriving a phase from tick arithmetic.
   *
   * `pos` is meaningful for `thunder` and `strike` (the firebolt's landing point) and is
   * the arena origin for the three phase edges, which have no place. It is never null,
   * because every consumer here wants a position it can hand to a spatial cue and the
   * three phase edges are global by nature — see `HitContact` for the opposite case,
   * where absence is real and is modelled as `null`.
   *
   * `thunder` fires on the tick a firebolt BEGINS its telegraph, never on its own. That
   * is the contract that makes the sound informative rather than decorative: every crack
   * of thunder is a strike that has already been aimed, and the §10 1.5 s floor is the
   * gap between the two. Nothing in this game makes a noise about danger that is not
   * arriving.
   */
  storm: { kind: 'onset' | 'peak' | 'clearing' | 'end' | 'thunder' | 'strike'; intensity: number; pos: Vec2 };
  pool_exhausted: { pool: string; denied_kind: string };
  /**
   * §6.6 / §14.1 B3 — a build-driven secondary spawn was trimmed on purpose.
   *
   * Deliberately NOT `pool_exhausted`: nothing was denied at the pool. The emitting system
   * looked at the live projectile count, decided the primary weapons need those slots more
   * than a Mitotic Bloom burst does, and spawned fewer shards than the build asked for.
   * `requested` and `spawned` are the counts for one burst, so a HUD can say "3 of 4" rather
   * than leave the player to infer that their boon stopped working.
   */
  budget_trim: { source: string; weapon_id: string; requested: number; spawned: number };
  game_over: { outcome: 'victory' | 'defeat' };
  level_up: { level: number };
  /** AMD-51 — an anomaly opened or closed. One start and one end per window, never per tick. */
  anomaly: { id: string; name: string; danger: number; phase: 'start' | 'end' };
  /** AMD-53 — a boss carcass was salvaged. Fires once per boss kill, after the ichor credit. */
  salvage: { reels: readonly number[]; tier: 'jackpot' | 'pair' | 'scraps'; ichor: number };
  /** AMD-34 — a numbered wave opens. Fires on the wave boundary, before `phase_change`. */
  wave_change: { wave: number; boss_wave: boolean; phase_id: string; duration_s: number };
  /** AMD-34 — the wave's internal beat changed. `muster` is the last 3 s of the lull. */
  wave_phase: { wave: number; segment: 'onset' | 'sustain' | 'lull' | 'muster'; remaining_s: number };
  /**
   * AMD-35 §9.12 — whole ichor was credited. Fires on the whole-unit crossing only, so
   * the trash trickle produces roughly one event per six kills rather than one per kill.
   * `pos` is the kill point; presentation draws the streak from there to the HUD counter.
   */
  gacha_ichor: { amount: number; balance: number; source: 'trash' | 'elite' | 'boss'; pos: Vec2 };
  /** AMD-35 §9.12 — the balance first covers the next Graft. One rising edge, one cue. */
  gacha_ready: { cost: number; balance: number; index: number };
  /**
   * AMD-35 §9.12 — a Graft resolved. Everything the reveal needs is in the payload; the
   * UI never re-derives a delta string. `delta_text` is never empty (§13.1.6).
   */
  gacha_pull: {
    index: number;
    kind: 'weapon' | 'passive' | 'evolution' | 'duplicate' | 'consolation';
    id: string;
    rarity: string;
    display_name: string;
    delta_text: string;
    level_from: number;
    level_to: number;
    pity_progress: number;
    pity_threshold: number;
    cost: number;
    balance_after: number;
  };
  /**
   * AMD-39 §9.13 — the Hold meter crossed an edge. Fires on the rising edge (`anchored`),
   * the falling edge (`moved`) and on the dash that sheds it (`dashed`); never per tick.
   * `payout` is `meter × threat` — the scalar every Holdfast reward axis multiplies.
   */
  hold_change: { engaged: boolean; meter: number; payout: number; threat: number; cause: string };
}

export type EventType = keyof EventPayloads;

export interface GameEvent<T extends EventType = EventType> {
  type: T;
  payload: EventPayloads[T];
  event_id: number;
  tick: number;
}

type Handler<T extends EventType> = (payload: EventPayloads[T], ev: GameEvent<T>) => void;

const MAX_QUEUE = 4096;

export class EventBus {
  private queue: { type: EventType; payload: unknown }[] = [];
  private handlers = new Map<EventType, Handler<EventType>[]>();
  private nextEventId = 1;
  private flushing = false;
  /** Set true in dev builds: queue runaway throws instead of dropping. */
  strict = false;

  on<T extends EventType>(type: T, fn: Handler<T>): () => void {
    let list = this.handlers.get(type);
    if (!list) {
      list = [];
      this.handlers.set(type, list);
    }
    list.push(fn as Handler<EventType>);
    return () => {
      const i = list!.indexOf(fn as Handler<EventType>);
      if (i >= 0) list!.splice(i, 1);
    };
  }

  emit<T extends EventType>(type: T, payload: EventPayloads[T]): void {
    if (this.queue.length >= MAX_QUEUE) {
      if (this.strict) throw new Error(`EventBus runaway: queue depth ${this.queue.length} emitting ${type}`);
      return; // drop-and-log in release
    }
    this.queue.push({ type, payload });
  }

  /** Drains the queue completely; events emitted by handlers are delivered in the same flush. */
  flush(tick: number): void {
    if (this.flushing) return;
    this.flushing = true;
    let guard = 0;
    while (this.queue.length > 0) {
      if (++guard > MAX_QUEUE * 4) {
        this.queue.length = 0;
        break;
      }
      const item = this.queue.shift()!;
      const ev: GameEvent = {
        type: item.type,
        payload: item.payload as EventPayloads[EventType],
        event_id: this.nextEventId++,
        tick,
      };
      const list = this.handlers.get(item.type);
      if (list) {
        for (let i = 0; i < list.length; i++) list[i](ev.payload, ev);
      }
    }
    this.flushing = false;
  }

  clear(): void {
    this.queue.length = 0;
  }

  reset(): void {
    this.clear();
    this.nextEventId = 1;
  }

  get depth(): number {
    return this.queue.length;
  }

  getEventIdCounter(): number {
    return this.nextEventId;
  }

  setEventIdCounter(v: number): void {
    this.nextEventId = v;
  }
}
